import React, { useState, useEffect } from 'react';
import { object } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withApollo } from 'react-apollo';
import { get, size, map, omitBy, isObject, isEmpty, head, sortBy } from 'lodash';
import { Main } from 'base-components';
import Filters from './Filters';
import PositionsTable from './PositionsTable';
import { NotFound, Pagination, FlexContainer, Loading, If } from '../../common';
import { MediaListener } from '../../utils';
import GET_POSITIONS from '../../graphql/getPositionsDarkstore';

const PositionsDarkstore = ({ session, client }) => {
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(null);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [totalPage, setTotalPage] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showMsgSelectFilter, setShowMsgSelectFilter] = useState(size(get(session, 'salesChannels')) > 1);
    const [cachedFilters, setCachedFilters] = useState({});
    const [sort, setSort] = useState({});
    const [loadingTable, setLoadingTable] = useState(false);
    const [disableTrash, setDisableTrash] = useState(true);
    const [queryValues, setQueryValues] = useState(null);

    useEffect(() => {
        const idStoreFilter = getInitialSaleChannelSelected();
        let filter = isEmpty(idStoreFilter) ? {} : { idStore: get(head(idStoreFilter), 'value', null) };
        getPositionsDarkStore({ ...{ size: pageSize }, ...{ filters: filter } });
    }, []);

    useEffect(() => {
        (currentPage !== null) && onSubmit(cachedFilters);
    }, [currentPage]);

    const salesChannelOptions = map(get(session, 'salesChannels'), ({ name, store, id }) => {
        return { label: `${store} - ${name}`, value: id };
    });

    const getPositionsDarkStore = async (variables, _, resetPage) => {
        const { page, ...moreVariables } = variables;
        await client.query({ query: GET_POSITIONS, variables: resetPage ? { ...moreVariables, page: 1 } : variables, fetchPolicy: 'network-only' })
            .then(({ data }) => {
                setPayload(get(data, 'getPositionsDarkStore.payload', null));
                setTotalRecords(get(data, 'getPositionsDarkStore.totalRecords', 0));
                setTotalPage(get(data, 'getPositionsDarkStore.totalPage', 0));
                setTimeout(() => {
                    setLoading(false);
                    setLoadingTable(false);
                }, 1000);
            });
    };

    const formatVariables = (variables, sortFromTable = {}) => {
        const newVariables = {
            size: pageSize,
            page: currentPage === null ? 1 : currentPage,
            sort: isEmpty(sortFromTable) ? sort : sortFromTable,
            filters: variables
        };

        return omitBy(newVariables, item =>
            // Se agrega isObject a la validacion porque, isEmpty considera vacio el 1
            isObject(item) && isEmpty(item)
        );
    };

    const onSubmit = (variables, resetPage = false) => {
        setQueryValues(variables);
        setCachedFilters(variables);
        setShowMsgSelectFilter(isEmpty(variables));
        getPositionsDarkStore(formatVariables(variables), isEmpty(sort), resetPage);
    };

    const getInitialSaleChannelSelected = () => {
        let salesChannels = get(session, 'salesChannels');

        if (!(size(salesChannels) === 1)) return [];

        return [{
            value: get(head(salesChannels), 'id'),
            label: get(head(salesChannels), 'name')
        }];
    };

    const PaginationComponent = () => (
        <Pagination
            defaultPageSize={100}
            pageSize={pageSize}
            onChangePageSize={({ value }) => {
                const sizeValue = get(value, 'value', 100);
                if (pageSize === 50 && sizeValue === 100) {
                    const maxFullSizePages = totalPage > parseInt(totalPage) ? parseInt(totalPage + 1) : parseInt(totalPage);
                    if (currentPage > totalPage) setCurrentPage(maxFullSizePages);
                }
                setPageSize(sizeValue);
            }}
            onChange={value => {
                setLoadingTable(true);
                setCurrentPage(value);
            }}
            current={currentPage == null ? 1 : currentPage}
            total={totalRecords}
            optionsUp
        />
    );

    return (
        <Main background='#f4f4f4' withFooter>
            <FlexContainer shrink={0} grow={1} style={{ padding: '5px 25px 0px' }} direction='column'>
                <MediaListener values={['13.55%', '100%', '100%']}>
                    {({ currentValue }) => (
                        <>
                            <Filters
                                currentValue={currentValue}
                                salesChannelOptions={sortBy(salesChannelOptions, 'label')}
                                onSubmit={(value) => onSubmit(value, true)}
                                initialSaleChannelSelected={getInitialSaleChannelSelected()}
                                showMsgSelectFilter={showMsgSelectFilter}
                                email={get(session, 'email')}
                                setLoading={setLoading}
                                setDisableTrash={setDisableTrash}
                                disableTrash={disableTrash}
                                session={session}
                                resetCurrentPage={() => setCurrentPage(1)}
                            />
                            { showMsgSelectFilter
                                ? <NotFound
                                    firstTextLine='Aún no se ha realizado una búsqueda, utiliza los filtros.'
                                    icon='find'
                                    iconBackgroundColor='#FFFFFF'
                                    iconColor='#c0c0c0'
                                    iconSize={85}
                                    fontSize={20}
                                />
                                : <Loading loading={loading}>
                                    <If
                                        condition={size(payload) > 0}
                                        then={(
                                            <>
                                                <PaginationComponent />
                                                <PositionsTable
                                                    onPaginationChange={() => {}}
                                                    data={payload}
                                                    onRowClick={() => {}}
                                                    count={totalRecords}
                                                    cachedFilters={cachedFilters}
                                                    getPositionsDarkStore={getPositionsDarkStore}
                                                    formatVariables={formatVariables}
                                                    setSort={setSort}
                                                    loadingTable={loadingTable}
                                                    disableTrash={disableTrash}
                                                    refetchQuery={() => onSubmit(queryValues)}
                                                />
                                                <PaginationComponent />
                                            </>
                                        )}
                                        else={(
                                            <NotFound
                                                firstTextLine='No se encontraron resultados'
                                                icon='cross'
                                                iconBackgroundColor='#eaeaea'
                                            />
                                        )}
                                    />
                                </Loading>
                            }
                        </>
                    )}
                </MediaListener>
            </FlexContainer>
        </Main>
    );
};

PositionsDarkstore.propTypes = {
    session: object,
    client: object
};

export default withRouter(withApollo(PositionsDarkstore));
